/**
 * Carryable, collectible, drill-site and ground-item meshes: placement (×2, surface-aligned),
 * baking, animation and disposal. The models themselves live in itemModels.ts (carryables,
 * drill sites) and projectileModels.ts (heal packs, shared with the shop thumbnails).
 */

import * as THREE from 'three';
import type { CarryableObjectState, CollectibleNode, DrillSiteState, CarryableType, GroundItemState } from '../types';
import { buildDataFragment, buildNavCore, buildTechSalvage, buildHullPlate, buildReactorShard, buildArtifact, buildDrillSiteModel, type ItemAnimData } from './itemModels';
import { buildHealthSmall, buildHealthLarge } from './projectileModels';
import { bakeStaticParts, disposeObjectTree } from './meshBake';

function matGlow(color: string): THREE.MeshLambertMaterial {
  return new THREE.MeshLambertMaterial({ color: new THREE.Color(color), emissive: new THREE.Color(color), emissiveIntensity: 0.7 });
}
/** The meshes the updaters move or pulse — kept out of the bake. */
function animatedParts(ad: ItemAnimData): THREE.Object3D[] {
  return ([ad.crystal, ad.holoRing, ...(ad.holoNodes ?? []), ad.ember] as Array<THREE.Object3D | undefined>).filter((o): o is THREE.Object3D => !!o);
}
// ---------------------------------------------------------------------------
// Interfaces
// ---------------------------------------------------------------------------

export type CarryableAnimData = ItemAnimData;
export interface CarryableMesh {
  group: THREE.Group;
  carryableId: number;
  animData: CarryableAnimData;
}

export interface CollectibleMesh {
  group: THREE.Group;
  collectibleId: number;
  animData: CarryableAnimData;
}

export interface DrillSiteMesh {
  group: THREE.Group;
  drillSiteId: number;
  buildingModel: THREE.Group;
  operatingModel: THREE.Group;
  extractedModel: THREE.Group;
  currentState: 'building' | 'operating' | 'extracted';
  animData: { drillBit?: THREE.Mesh; ptLight?: THREE.PointLight };
}

// ============================================================
// SALVAGE VARIANT MAPPING
// ============================================================

function buildSalvageVariant(variantId: number): { group: THREE.Group; animData: CarryableAnimData } {
  const variant = variantId % 3;
  switch (variant) {
    case 0: return buildTechSalvage();
    case 1: return buildHullPlate();
    case 2: return buildReactorShard();
    default: return buildTechSalvage();
  }
}

// ============================================================
// CARRIER INDICATOR
// ============================================================

export function getCarrierIndicatorColor(type: CarryableType): string {
  switch (type) {
    case 'navCore': return '#ff8830';
    case 'dataFragment': return '#50a8ff';
    case 'techSalvage':
    case 'salvageNode': return '#30cc30';
    case 'artifact': return '#ffc040';
    default: return '#ffffff';
  }
}

export function createCarrierIndicator(type: CarryableType): THREE.Mesh {
  const color = getCarrierIndicatorColor(type);
  const indicator = new THREE.Mesh(
    new THREE.SphereGeometry(0.03, 8, 6),
    matGlow(color)
  );
  indicator.position.y = 0.8;
  return indicator;
}

// ============================================================
// CREATE / UPDATE / DISPOSE — CARRYABLE MESHES
// ============================================================

export function createCarryableMesh(
  carryable: CarryableObjectState,
  worldPos: THREE.Vector3,
  surfaceNormal: THREE.Vector3,
  scene: THREE.Scene
): CarryableMesh {
  let result: { group: THREE.Group; animData: CarryableAnimData };

  switch (carryable.type) {
    case 'navCore':
      result = buildNavCore(carryable.teamIndex);
      break;
    case 'dataFragment':
      result = buildDataFragment(carryable.teamIndex, false);
      break;
    case 'techSalvage':
    case 'salvageNode':
      result = buildSalvageVariant(carryable.id);
      break;
    case 'artifact':
      result = buildArtifact();
      break;
    default:
      result = buildTechSalvage();
  }

  bakeStaticParts(result.group, { exclude: animatedParts(result.animData) });
  const container = new THREE.Group();
  container.add(result.group);
  container.position.copy(worldPos).addScaledVector(surfaceNormal, 0.05);

  const up = new THREE.Vector3(0, 1, 0);
  container.quaternion.setFromUnitVectors(up, surfaceNormal);
  container.scale.setScalar(2);

  scene.add(container);

  return { group: container, carryableId: carryable.id, animData: result.animData };
}

export function updateCarryableMeshes(
  meshes: Map<number, CarryableMesh>,
  _dt: number,
  time: number
): void {
  for (const [, cm] of meshes) {
    const ad = cm.animData;

    // Data fragment crystal rotation
    if (ad.crystal) {
      ad.crystal.rotation.y += 0.008;
    }

    // Nav core holo ring rotation + node orbits
    if (ad.holoRing) {
      ad.holoRing.rotation.z += 0.01;
    }
    if (ad.holoNodes) {
      const hy = ad.bodyY ?? 0.09;
      for (const node of ad.holoNodes) {
        if (node.userData.nodeAngle !== undefined) {
          node.userData.nodeAngle += 0.01;
          const a = node.userData.nodeAngle;
          node.position.x = Math.cos(a) * 0.13;
          node.position.z = Math.sin(a) * 0.13;
          node.position.y = hy + Math.sin(a * 3 + time) * 0.008;
        }
      }
    }

    // Reactor shard ember pulse
    if (ad.ember) {
      const pulse = 0.6 + Math.sin(time * 1.5) * 0.15;
      (ad.ember.material as THREE.MeshLambertMaterial).emissiveIntensity = pulse;
      if (ad.ptLight) {
        ad.ptLight.intensity = 0.35 + Math.sin(time * 1.5) * 0.15;
      }
    }
  }
}

export function disposeCarryableMesh(scene: THREE.Scene, mesh: CarryableMesh): void {
  scene.remove(mesh.group);
  disposeObjectTree(mesh.group);
}

// ============================================================
// CREATE / UPDATE / DISPOSE — COLLECTIBLE MESHES
// ============================================================

export function createCollectibleMesh(
  collectible: CollectibleNode,
  worldPos: THREE.Vector3,
  surfaceNormal: THREE.Vector3,
  scene: THREE.Scene
): CollectibleMesh {
  let result: { group: THREE.Group; animData: CarryableAnimData };

  switch (collectible.type) {
    case 'dataFragment':
      result = buildDataFragment(0, true); // vaulted pedestal
      break;
    case 'salvageNode':
      result = buildSalvageVariant(collectible.id);
      break;
    default:
      result = buildTechSalvage();
  }

  bakeStaticParts(result.group, { exclude: animatedParts(result.animData) });
  const container = new THREE.Group();
  container.add(result.group);
  container.position.copy(worldPos).addScaledVector(surfaceNormal, 0.05);

  const up = new THREE.Vector3(0, 1, 0);
  container.quaternion.setFromUnitVectors(up, surfaceNormal);
  container.scale.setScalar(2);

  scene.add(container);

  return { group: container, collectibleId: collectible.id, animData: result.animData };
}

export function updateCollectibleMeshes(
  meshes: Map<number, CollectibleMesh>,
  _dt: number,
  time: number
): void {
  for (const [, cm] of meshes) {
    const ad = cm.animData;

    // Data fragment crystal rotation
    if (ad.crystal) {
      ad.crystal.rotation.y += 0.008;
    }

    // Reactor shard ember pulse
    if (ad.ember) {
      const pulse = 0.6 + Math.sin(time * 1.5) * 0.15;
      (ad.ember.material as THREE.MeshLambertMaterial).emissiveIntensity = pulse;
      if (ad.ptLight) {
        ad.ptLight.intensity = 0.35 + Math.sin(time * 1.5) * 0.15;
      }
    }
  }
}

export function disposeCollectibleMesh(scene: THREE.Scene, mesh: CollectibleMesh): void {
  scene.remove(mesh.group);
  disposeObjectTree(mesh.group);
}

// ============================================================
// CREATE / UPDATE / DISPOSE — DRILL SITE MESHES
// ============================================================

export function createDrillSiteMesh(
  drill: DrillSiteState,
  teamIdx: number,
  worldPos: THREE.Vector3,
  surfaceNormal: THREE.Vector3,
  scene: THREE.Scene
): DrillSiteMesh {
  const { buildingModel, operatingModel, extractedModel, animData } = buildDrillSiteModel(teamIdx);
  for (const m of [buildingModel, operatingModel, extractedModel]) bakeStaticParts(m, { exclude: [animData.drillBit] });   // before visibility is set: hidden meshes are skipped by the bake

  const container = new THREE.Group();
  container.add(buildingModel);
  container.add(operatingModel);
  container.add(extractedModel);

  // Set initial visibility
  const state = drill.extracted ? 'extracted' : drill.operating ? 'operating' : 'building';
  buildingModel.visible = state === 'building';
  operatingModel.visible = state === 'operating';
  extractedModel.visible = state === 'extracted';

  container.position.copy(worldPos).addScaledVector(surfaceNormal, 0.05);

  const up = new THREE.Vector3(0, 1, 0);
  container.quaternion.setFromUnitVectors(up, surfaceNormal);
  container.scale.setScalar(2);

  scene.add(container);

  return {
    group: container,
    drillSiteId: drill.id,
    buildingModel,
    operatingModel,
    extractedModel,
    currentState: state,
    animData,
  };
}

export function updateDrillSiteMesh(
  mesh: DrillSiteMesh,
  drill: DrillSiteState,
  _dt: number,
  time: number
): void {
  // Update state visibility
  const newState = drill.extracted ? 'extracted' : drill.operating ? 'operating' : 'building';
  if (newState !== mesh.currentState) {
    mesh.buildingModel.visible = newState === 'building';
    mesh.operatingModel.visible = newState === 'operating';
    mesh.extractedModel.visible = newState === 'extracted';
    mesh.currentState = newState;
  }

  // Animate operating state
  if (newState === 'operating') {
    // Rotate drill bit
    if (mesh.animData.drillBit) {
      mesh.animData.drillBit.rotation.y += 0.05;
    }
    // Pulse status light
    if (mesh.animData.ptLight) {
      mesh.animData.ptLight.intensity = 0.3 + Math.sin(time * 2) * 0.15;
    }
  }
}

export function disposeDrillSiteMesh(scene: THREE.Scene, mesh: DrillSiteMesh): void {
  scene.remove(mesh.group);
  disposeObjectTree(mesh.group);
}

// ============================================================
// GROUND ITEMS (dropped heals)
// ============================================================

export interface GroundItemMesh {
  group: THREE.Group;
  groundItemId: number;
  cross: THREE.Mesh;
}

export function createGroundItemMesh(
  item: GroundItemState,
  worldPos: THREE.Vector3,
  surfaceNormal: THREE.Vector3,
  scene: THREE.Scene
): GroundItemMesh {
  const isLarge = item.type === 'healLarge';
  const model = isLarge ? buildHealthLarge() : buildHealthSmall();   // the shop models, so a dropped item matches its thumbnail
  bakeStaticParts(model);

  const container = new THREE.Group();
  container.add(model);
  container.position.copy(worldPos).addScaledVector(surfaceNormal, 0.05);

  const up = new THREE.Vector3(0, 1, 0);
  container.quaternion.setFromUnitVectors(up, surfaceNormal);
  container.scale.setScalar(2);

  scene.add(container);

  let cross: THREE.Mesh | null = null;
  model.traverse(o => { if (!cross && (o as THREE.Mesh).isMesh) cross = o as THREE.Mesh; });
  return { group: container, groundItemId: item.id, cross: cross! };
}

export function updateGroundItemMeshes(
  meshes: Map<number, GroundItemMesh>,
  _dt: number,
  time: number
): void {
  for (const [, mesh] of meshes) {
    // Slow rotation
    if (mesh.cross) {
      mesh.cross.parent!.rotation.y = time * 0.8;
    }
    // Pulse glow
    const pulse = 0.4 + 0.3 * Math.sin(time * 3);
    mesh.group.traverse((obj) => {
      if (obj instanceof THREE.Mesh && obj.material instanceof THREE.MeshLambertMaterial) {
        obj.material.emissiveIntensity = pulse;
      }
    });
  }
}

export function disposeGroundItemMesh(scene: THREE.Scene, mesh: GroundItemMesh): void {
  scene.remove(mesh.group);
  disposeObjectTree(mesh.group);
}
